'use client';
import React, { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { DollarSign, Heart, ChevronDown, ChevronUp, Users, Zap, BarChart2, ExternalLink, Edit2, Trash2, Check, X, FileText, ShieldCheck, Lock, Unlock, UserPlus, UserMinus, Loader2, BadgeCheck, AlertCircle } from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';
import StarRating from '@/components/ui/StarRating';
import LikeButton from '@/components/ui/LikeButton';
import { createClient } from '@/lib/supabase/client';
import type { Solution } from './mockData';

interface SolutionCardProps {
  solution: Solution;
  currentUserId: string | null;
  onFundClick: () => void;
  onUpdate: (updated: Solution) => void;
  onDelete: (solutionId: string) => void;
}

type DocType = 'business_plan' | 'police_clearance';

interface DocPermState {
  hasPermission: boolean;
  loading: boolean;
}

const IS_UUID = (id: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);

// ─── Permission Management Modal ────────────────────────────────────────────
interface PermissionModalProps {
  solutionId: string;
  docType: DocType;
  currentUserId: string;
  onClose: () => void;
}

function PermissionModal({ solutionId, docType, currentUserId, onClose }: PermissionModalProps) {
  const [grantedUsers, setGrantedUsers] = useState<{ id: string; user_id: string; full_name: string; email: string }[]>([]);
  const [emailInput, setEmailInput] = useState('');
  const [loadingGrant, setLoadingGrant] = useState(false);
  const [loadingList, setLoadingList] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const label = docType === 'business_plan' ? 'Business Plan' : 'Police Clearance';

  const fetchGranted = useCallback(async () => {
    setLoadingList(true);
    const supabase = createClient();
    const { data } = await supabase
      .from('document_permissions')
      .select('id, granted_to_user_id, user_profiles!document_permissions_granted_to_user_id_fkey(full_name, email)')
      .eq('solution_id', solutionId)
      .eq('document_type', docType);

    if (data) {
      setGrantedUsers(
        data.map((row: any) => ({
          id: row.id,
          user_id: row.granted_to_user_id,
          full_name: row.user_profiles?.full_name || 'Unknown',
          email: row.user_profiles?.email || '',
        }))
      );
    }
    setLoadingList(false);
  }, [solutionId, docType]);

  useEffect(() => { fetchGranted(); }, [fetchGranted]);

  async function handleGrant() {
    if (!emailInput.trim()) return;
    setError(''); setSuccess(''); setLoadingGrant(true);
    const supabase = createClient();
    // Find user by email
    const { data: profile, error: profileErr } = await supabase
      .from('user_profiles')
      .select('id, full_name')
      .eq('email', emailInput.trim().toLowerCase())
      .single();

    if (profileErr || !profile) {
      setError('No user found with that email.');
      setLoadingGrant(false);
      return;
    }

    const { error: insertErr } = await supabase.from('document_permissions').insert({
      solution_id: solutionId,
      granted_to_user_id: profile.id,
      granted_by_user_id: currentUserId,
      document_type: docType,
    });

    if (insertErr) {
      setError(insertErr.message.includes('unique') ? 'User already has access.' : insertErr.message);
    } else {
      setSuccess(`Access granted to ${profile.full_name}.`);
      setEmailInput('');
      fetchGranted();
    }
    setLoadingGrant(false);
  }

  async function handleRevoke(permId: string) {
    const supabase = createClient();
    await supabase.from('document_permissions').delete().eq('id', permId);
    fetchGranted();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="bg-card border border-border rounded-xl shadow-xl w-full max-w-md p-5 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-foreground flex items-center gap-2">
            {docType === 'business_plan' ? <FileText size={14} className="text-primary" /> : <ShieldCheck size={14} className="text-emerald-500" />}
            Manage Access — {label}
          </h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-muted transition-colors">
            <X size={14} className="text-muted-foreground" />
          </button>
        </div>

        {/* Grant by email */}
        <div className="space-y-2">
          <label className="text-xs font-semibold text-foreground">Grant access by email</label>
          <div className="flex gap-2">
            <input
              type="email"
              value={emailInput}
              onChange={(e) => { setEmailInput(e.target.value); setError(''); setSuccess(''); }}
              placeholder="user@example.com"
              className="flex-1 px-2.5 py-1.5 rounded-lg border border-border text-xs text-foreground bg-background focus:outline-none focus:ring-2 focus:ring-primary transition-all"
            />
            <button
              onClick={handleGrant}
              disabled={loadingGrant || !emailInput.trim()}
              className="flex items-center gap-1 px-3 py-1.5 bg-primary text-primary-foreground text-xs font-bold rounded-lg hover:bg-cyan-600 transition-colors disabled:opacity-60"
            >
              {loadingGrant ? <Loader2 size={11} className="animate-spin" /> : <UserPlus size={11} />}
              Grant
            </button>
          </div>
          {error && <p className="text-xs text-red-500">{error}</p>}
          {success && <p className="text-xs text-emerald-600">{success}</p>}
        </div>

        {/* Granted users list */}
        <div className="space-y-1.5">
          <p className="text-xs font-semibold text-foreground">Users with access</p>
          {loadingList ? (
            <div className="flex items-center gap-2 py-3 text-xs text-muted-foreground">
              <Loader2 size={12} className="animate-spin" /> Loading…
            </div>
          ) : grantedUsers.length === 0 ? (
            <p className="text-xs text-muted-foreground py-2">No users have been granted access yet.</p>
          ) : (
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {grantedUsers.map((u) => (
                <div key={u.id} className="flex items-center justify-between bg-muted/40 rounded-lg px-2.5 py-1.5">
                  <div>
                    <p className="text-xs font-medium text-foreground">{u.full_name}</p>
                    <p className="text-xs text-muted-foreground">{u.email}</p>
                  </div>
                  <button
                    onClick={() => handleRevoke(u.id)}
                    className="p-1 rounded hover:bg-red-100 transition-colors"
                    title="Revoke access"
                  >
                    <UserMinus size={12} className="text-red-500" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Request Access Modal ─────────────────────────────────────────────────────
interface RequestAccessModalProps {
  label: string;
  docType: DocType;
  solutionId: string;
  currentUserId: string | null;
  onClose: () => void;
}

function RequestAccessModal({ label, docType, solutionId, currentUserId, onClose }: RequestAccessModalProps) {
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const icon = docType === 'business_plan'
    ? <FileText size={20} className="text-primary" />
    : <ShieldCheck size={20} className="text-emerald-500" />;

  async function handleRequest(e: React.FormEvent) {
    e.preventDefault();
    if (!currentUserId) {
      setError('You must be logged in to request access.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const supabase = createClient();
      // Check if a request already exists
      const { data: existing } = await supabase
        .from('document_access_requests')
        .select('id')
        .eq('solution_id', solutionId)
        .eq('requested_by_user_id', currentUserId)
        .eq('document_type', docType)
        .maybeSingle();

      if (existing) {
        setError('You have already submitted a request for this document.');
        setSubmitting(false);
        return;
      }

      await supabase.from('document_access_requests').insert({
        solution_id: solutionId,
        requested_by_user_id: currentUserId,
        document_type: docType,
        message: message.trim() || null,
      });
      setSubmitted(true);
    } catch {
      setError('Failed to submit request. Please try again.');
    }
    setSubmitting(false);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="bg-card border border-border rounded-xl shadow-xl w-full max-w-sm p-6 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        {submitted ? (
          <div className="flex flex-col items-center gap-3 py-4 text-center">
            <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
              <Check size={22} className="text-emerald-600" />
            </div>
            <h3 className="text-sm font-bold text-foreground">Request Sent!</h3>
            <p className="text-xs text-muted-foreground">
              Your request to access the <span className="font-semibold text-foreground">{label}</span> has been submitted. The Monezite admin team will review it and grant access if approved.
            </p>
            <button
              onClick={onClose}
              className="mt-2 px-5 py-2 bg-primary text-primary-foreground text-xs font-bold rounded-lg hover:bg-cyan-600 transition-colors"
            >
              Close
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                {icon}
                <h3 className="text-sm font-bold text-foreground">Request Access</h3>
              </div>
              <button onClick={onClose} className="p-1 rounded hover:bg-muted transition-colors">
                <X size={14} className="text-muted-foreground" />
              </button>
            </div>

            <p className="text-xs text-muted-foreground leading-relaxed">
              This document is restricted. Submit a request to <span className="font-semibold text-foreground">Monezite</span> to gain access to the{' '}
              <span className="font-semibold text-foreground">{label}</span>. Our team will review your request and notify you.
            </p>

            <form onSubmit={handleRequest} className="space-y-3">
              <div className="space-y-1">
                <label className="text-xs font-semibold text-foreground">Message (optional)</label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Briefly explain why you need access…"
                  rows={3}
                  className="w-full px-3 py-2 rounded-lg border border-border text-xs text-foreground bg-background focus:outline-none focus:ring-2 focus:ring-primary transition-all resize-none"
                />
              </div>

              {error && <p className="text-xs text-red-500">{error}</p>}

              <div className="flex gap-2 pt-1">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-4 py-2 text-xs font-semibold border border-border text-foreground rounded-lg hover:bg-muted transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2 bg-primary text-primary-foreground text-xs font-bold rounded-lg hover:bg-cyan-600 transition-colors disabled:opacity-60"
                >
                  {submitting ? <Loader2 size={11} className="animate-spin" /> : null}
                  Request Access
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Document Button ─────────────────────────────────────────────────────────
interface DocButtonProps {
  label: string;
  docType: DocType;
  solutionId: string;
  currentUserId: string | null;
  isOwner: boolean;
  isAdmin: boolean;
  isDbSolution: boolean;
}

function DocButton({ label, docType, solutionId, currentUserId, isOwner, isAdmin, isDbSolution }: DocButtonProps) {
  const [permState, setPermState] = useState<DocPermState>({ hasPermission: false, loading: true });
  const [showModal, setShowModal] = useState(false);
  const [showDenied, setShowDenied] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);

  const canManage = isOwner || isAdmin;
  const icon = docType === 'business_plan'
    ? <FileText size={11} />
    : <ShieldCheck size={11} />;

  useEffect(() => {
    if (!isDbSolution || !currentUserId || canManage) {
      setPermState({ hasPermission: canManage, loading: false });
      return;
    }
    let cancelled = false;
    const supabase = createClient();
    supabase
      .from('document_permissions')
      .select('id')
      .eq('solution_id', solutionId)
      .eq('granted_to_user_id', currentUserId)
      .eq('document_type', docType)
      .maybeSingle()
      .then(({ data }) => {
        if (!cancelled) {
          setPermState({ hasPermission: !!data, loading: false });
        }
      });
    return () => { cancelled = true; };
  }, [solutionId, currentUserId, docType, canManage, isDbSolution]);

  function handleClick() {
    if (permState.loading) return;
    if (canManage) {
      // Owner/admin opens permission management modal
      setShowModal(true);
    } else if (permState.hasPermission) {
      // Permitted user: open the document (placeholder — no actual file yet)
      alert(`Opening ${label}…\n\n(Document viewer coming soon)`);
    } else {
      // No permission — show request access modal
      setShowRequestModal(true);
    }
  }

  const isLocked = !canManage && !permState.hasPermission;

  return (
    <>
      <div className="relative flex-1">
        <button
          onClick={handleClick}
          disabled={permState.loading}
          title={
            canManage
              ? `Manage access to ${label}`
              : permState.hasPermission
              ? `Open ${label}`
              : `You need permission to view ${label}`
          }
          className={`w-full flex items-center justify-center gap-1.5 py-2 text-xs font-semibold rounded-lg border transition-all duration-150
            ${canManage
              ? docType === 'business_plan' ?'border-primary/40 text-primary hover:bg-primary/10' :'border-emerald-400/40 text-emerald-600 hover:bg-emerald-50'
              : permState.hasPermission
              ? docType === 'business_plan' ?'border-primary/40 text-primary hover:bg-primary/10' :'border-emerald-400/40 text-emerald-600 hover:bg-emerald-50' :'border-border text-muted-foreground hover:bg-muted/50 hover:border-primary/30'
            }`}
        >
          {permState.loading ? (
            <Loader2 size={11} className="animate-spin" />
          ) : isLocked ? (
            <Lock size={11} />
          ) : canManage ? (
            <Unlock size={11} />
          ) : (
            icon
          )}
          <span className="truncate">{label}</span>
          {canManage && (
            <span className="ml-auto text-[9px] font-bold uppercase tracking-wide opacity-60">Manage</span>
          )}
        </button>

      </div>

      {showModal && currentUserId && (
        <PermissionModal
          solutionId={solutionId}
          docType={docType}
          currentUserId={currentUserId}
          onClose={() => setShowModal(false)}
        />
      )}

      {showRequestModal && (
        <RequestAccessModal
          label={label}
          docType={docType}
          solutionId={solutionId}
          currentUserId={currentUserId}
          onClose={() => setShowRequestModal(false)}
        />
      )}
    </>
  );
}

// ─── Main SolutionCard ────────────────────────────────────────────────────────
export default function SolutionCard({ solution, currentUserId, onFundClick, onUpdate, onDelete }: SolutionCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [ownerIsVerified, setOwnerIsVerified] = useState(false);
  const [editTitle, setEditTitle] = useState(solution.title);
  const [editExcerpt, setEditExcerpt] = useState(solution.excerpt);
  const [editSolution, setEditSolution] = useState(solution.solution || '');
  const [editProductService, setEditProductService] = useState(solution.productService || '');
  const [editMarketSize, setEditMarketSize] = useState(solution.marketSize || '');
  const [editCompetition, setEditCompetition] = useState(solution.competition || '');
  const [editFundingNeeded, setEditFundingNeeded] = useState(solution.fundingNeeded || '');
  const [editPricePerShare, setEditPricePerShare] = useState(solution.pricePerShare || '');
  const [editInvest, setEditInvest] = useState(solution.invest || '');
  const [editError, setEditError] = useState('');
  const [editRevenue, setEditRevenue] = useState<{ m6: string; m12: string; m18: string; m24: string }>(
    solution.revenue ? { ...solution.revenue } : { m6: '', m12: '', m18: '', m24: '' }
  );
  const [editExpenses, setEditExpenses] = useState<{ m6: string; m12: string; m18: string; m24: string }>(
    solution.expenses ? { ...solution.expenses } : { m6: '', m12: '', m18: '', m24: '' }
  );
  const [editProfit, setEditProfit] = useState<{ m6: string; m12: string; m18: string; m24: string }>(
    solution.profit ? { ...solution.profit } : { m6: '', m12: '', m18: '', m24: '' }
  );

  const fundingPct = solution.fundingAsk > 0
    ? Math.round((solution.fundingOffered / solution.fundingAsk) * 100)
    : 0;

  const hasDetails = solution.problem || solution.solution || solution.productService ||
    solution.marketSize || solution.competition || solution.fundingNeeded ||
    solution.pricePerShare || solution.revenue;

  const isDbSolution = IS_UUID(solution.id);

  const isOwner = isDbSolution
    ? !!(currentUserId && solution.userId === currentUserId)
    : solution.entrepreneur === 'You';

  // Check admin status once
  useEffect(() => {
    if (!currentUserId || !isDbSolution) return;
    const supabase = createClient();
    supabase
      .from('user_profiles')
      .select('is_admin')
      .eq('id', currentUserId)
      .single()
      .then(({ data }) => {
        if (data?.is_admin) setIsAdmin(true);
      });
  }, [currentUserId, isDbSolution]);

  // Fetch owner verification status
  useEffect(() => {
    if (!isDbSolution || !solution.userId) return;
    const supabase = createClient();
    supabase
      .from('user_profiles')
      .select('is_verified')
      .eq('id', solution.userId)
      .single()
      .then(({ data }) => {
        if (data?.is_verified) setOwnerIsVerified(true);
      });
  }, [isDbSolution, solution.userId]);

  function calcEditProfit(rev: typeof editRevenue, exp: typeof editExpenses) {
    const result = { m6: '', m12: '', m18: '', m24: '' };
    (['m6', 'm12', 'm18', 'm24'] as const).forEach((k) => {
      const r = parseFloat(rev[k].replace(/[^0-9.-]/g, '')) || 0;
      const e = parseFloat(exp[k].replace(/[^0-9.-]/g, '')) || 0;
      result[k] = (r - e) !== 0 ? String(r - e) : '';
    });
    return result;
  }

  function handleRevenueChange(key: 'm6' | 'm12' | 'm18' | 'm24', value: string) {
    const newRev = { ...editRevenue, [key]: value };
    setEditRevenue(newRev);
    setEditProfit(calcEditProfit(newRev, editExpenses));
  }

  function handleExpensesChange(key: 'm6' | 'm12' | 'm18' | 'm24', value: string) {
    const newExp = { ...editExpenses, [key]: value };
    setEditExpenses(newExp);
    setEditProfit(calcEditProfit(editRevenue, newExp));
  }

  async function handleEditSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!editTitle.trim()) { setEditError('Title is required.'); return; }
    if (!editExcerpt.trim()) { setEditError('Excerpt is required.'); return; }
    setEditError('');

    const updated: Solution = {
      ...solution,
      title: editTitle,
      excerpt: editExcerpt,
      solution: editSolution,
      productService: editProductService,
      marketSize: editMarketSize,
      competition: editCompetition,
      fundingNeeded: editFundingNeeded,
      pricePerShare: editPricePerShare,
      invest: editInvest,
      revenue: editRevenue,
      expenses: editExpenses,
      profit: editProfit,
    };

    if (isDbSolution) {
      setSaving(true);
      try {
        const supabase = createClient();
        await supabase.from('solutions').update({
          title: editTitle,
          excerpt: editExcerpt,
          solution_description: editSolution,
          product_service: editProductService,
          market_size: editMarketSize,
          competition: editCompetition,
          funding_needed: parseFloat(editFundingNeeded.replace(/[^0-9.]/g, '')) || 0,
          price_per_share: parseFloat(editPricePerShare.replace(/[^0-9.]/g, '')) || 0,
          revenue_projections: editRevenue,
          expenses_projections: editExpenses,
          profit_projections: editProfit,
          updated_at: new Date().toISOString(),
        }).eq('id', solution.id);
      } finally {
        setSaving(false);
      }
    }

    onUpdate(updated);
    setShowEditForm(false);
  }

  async function handleDelete() {
    if (isDbSolution) {
      setDeleting(true);
      try {
        const supabase = createClient();
        await supabase.from('solutions').delete().eq('id', solution.id);
      } finally {
        setDeleting(false);
      }
    }
    onDelete(solution.id);
  }

  async function handleVerify() {
    if (!isDbSolution || !isOwner) return;
    setVerifying(true);
    try {
      const supabase = createClient();
      const newVerified = !solution.entrepreneurVerified;
      const { error } = await supabase
        .from('solutions')
        .update({ entrepreneur_verified: newVerified, updated_at: new Date().toISOString() })
        .eq('id', solution.id);
      if (!error) {
        onUpdate({ ...solution, entrepreneurVerified: newVerified });
      }
    } finally {
      setVerifying(false);
    }
  }

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden hover:border-primary/30 hover:card-shadow-md transition-all duration-200">
      {/* Solution Image */}
      {solution.image && (
        <div className="h-36 bg-muted overflow-hidden">
          <img
            src={solution.image}
            alt={solution.imageAlt || solution.title}
            className="w-full h-full object-cover"
          />
        </div>
      )}

      <div className="p-4 space-y-3">
        {/* Header */}
        <div className="flex items-start justify-between gap-2">
          <StatusBadge variant={solution.status} />
          <div className="flex items-center gap-1">
            {isOwner && (
              <>
                <button
                  onClick={() => { setShowEditForm((v) => !v); setDeleteConfirm(false); }}
                  className={`p-1 rounded hover:bg-muted transition-colors ${showEditForm ? 'text-primary' : 'text-muted-foreground'}`}
                  aria-label="Edit solution"
                  title="Edit your solution"
                >
                  <Edit2 size={13} />
                </button>
                <button
                  onClick={() => { setDeleteConfirm((v) => !v); setShowEditForm(false); }}
                  className={`p-1 rounded hover:bg-danger/10 transition-colors ${deleteConfirm ? 'text-danger' : 'text-muted-foreground hover:text-danger'}`}
                  aria-label="Delete solution"
                  title="Delete your solution"
                >
                  <Trash2 size={13} />
                </button>
              </>
            )}
            {isDbSolution ? (
              <LikeButton
                targetType="solution"
                targetId={solution.id}
                initialCount={0}
                size={13}
                showCount={false}
              />
            ) : (
              <button
                className="p-1 rounded hover:bg-muted transition-colors shrink-0"
                aria-label="Like solution"
              >
                <Heart size={13} className="text-muted-foreground" />
              </button>
            )}
          </div>
        </div>

        {/* Entrepreneur Verification — owner sees Verify button, others see status badge */}
        {isOwner ? (
          <button
            onClick={handleVerify}
            disabled={verifying}
            className={`w-full flex items-center justify-center gap-1.5 py-2 text-xs font-semibold rounded-lg border transition-all duration-150 ${
              solution.entrepreneurVerified
                ? 'border-emerald-400/50 bg-emerald-50 text-emerald-700 hover:bg-emerald-100' :'border-amber-400/50 bg-amber-50 text-amber-700 hover:bg-amber-100'
            }`}
            title={solution.entrepreneurVerified ? 'Click to unverify yourself' : 'Click to verify yourself as the entrepreneur'}
          >
            {verifying ? (
              <Loader2 size={12} className="animate-spin" />
            ) : solution.entrepreneurVerified ? (
              <BadgeCheck size={13} className="text-emerald-600" />
            ) : (
              <AlertCircle size={13} className="text-amber-600" />
            )}
            {solution.entrepreneurVerified ? 'Entrepreneur Verified (click to unverify)' : 'Verify as Entrepreneur'}
          </button>
        ) : (
          <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold w-fit ${
            solution.entrepreneurVerified
              ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :'bg-muted text-muted-foreground border border-border'
          }`}>
            {solution.entrepreneurVerified ? (
              <><BadgeCheck size={12} className="text-emerald-600" /> Entrepreneur Verified</>
            ) : (
              <><AlertCircle size={12} className="text-amber-500" /> Not Verified</>
            )}
          </div>
        )}

        {/* Admin-verified identity badge */}
        {ownerIsVerified && (
          <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold w-fit bg-primary/10 text-primary border border-primary/20">
            <ShieldCheck size={12} className="text-primary" />
            Identity Verified
          </div>
        )}

        {/* Delete Confirmation */}
        {deleteConfirm && isOwner && (
          <div className="bg-danger/5 border border-danger/20 rounded-lg p-3 space-y-2">
            <p className="text-xs font-semibold text-danger">Delete this solution?</p>
            <p className="text-xs text-muted-foreground">This cannot be undone.</p>
            <div className="flex gap-2">
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex items-center gap-1 px-3 py-1.5 bg-danger text-white text-xs font-bold rounded-lg hover:bg-red-600 transition-colors disabled:opacity-60"
              >
                {deleting ? 'Deleting…' : <><Trash2 size={11} /> Delete</>}
              </button>
              <button
                onClick={() => setDeleteConfirm(false)}
                className="px-3 py-1.5 bg-muted text-foreground text-xs font-semibold rounded-lg hover:bg-muted/80 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Inline Edit Form */}
        {showEditForm && isOwner && (
          <form onSubmit={handleEditSubmit} className="bg-muted/40 border border-border rounded-lg p-3 space-y-2">
            <p className="text-xs font-bold text-foreground flex items-center gap-1"><Edit2 size={11} className="text-primary" /> Edit Solution</p>

            <div>
              <label className="text-xs text-muted-foreground mb-0.5 block">Title <span className="text-danger">*</span></label>
              <input
                type="text"
                value={editTitle}
                onChange={(e) => { setEditTitle(e.target.value); setEditError(''); }}
                className="w-full px-2.5 py-1.5 rounded-lg border border-border text-xs text-foreground bg-background focus:outline-none focus:ring-2 focus:ring-primary transition-all"
              />
            </div>

            <div>
              <label className="text-xs text-muted-foreground mb-0.5 block">Excerpt / Summary <span className="text-danger">*</span></label>
              <textarea
                value={editExcerpt}
                onChange={(e) => { setEditExcerpt(e.target.value); setEditError(''); }}
                rows={2}
                className="w-full px-2.5 py-1.5 rounded-lg border border-border text-xs text-foreground bg-background resize-none focus:outline-none focus:ring-2 focus:ring-primary transition-all"
              />
            </div>

            <div>
              <label className="text-xs text-muted-foreground mb-0.5 block">Proposed Solution</label>
              <textarea
                value={editSolution}
                onChange={(e) => setEditSolution(e.target.value)}
                rows={2}
                placeholder="Describe your solution…"
                className="w-full px-2.5 py-1.5 rounded-lg border border-border text-xs text-foreground bg-background resize-none focus:outline-none focus:ring-2 focus:ring-primary transition-all"
              />
            </div>

            <div>
              <label className="text-xs text-muted-foreground mb-0.5 block">Product / Service</label>
              <textarea
                value={editProductService}
                onChange={(e) => setEditProductService(e.target.value)}
                rows={2}
                placeholder="Describe your product or service…"
                className="w-full px-2.5 py-1.5 rounded-lg border border-border text-xs text-foreground bg-background resize-none focus:outline-none focus:ring-2 focus:ring-primary transition-all"
              />
            </div>

            <div>
              <label className="text-xs text-muted-foreground mb-0.5 block">Market Size</label>
              <textarea
                value={editMarketSize}
                onChange={(e) => setEditMarketSize(e.target.value)}
                rows={2}
                placeholder="Describe the target market…"
                className="w-full px-2.5 py-1.5 rounded-lg border border-border text-xs text-foreground bg-background resize-none focus:outline-none focus:ring-2 focus:ring-primary transition-all"
              />
            </div>

            <div>
              <label className="text-xs text-muted-foreground mb-0.5 block">Competition</label>
              <textarea
                value={editCompetition}
                onChange={(e) => setEditCompetition(e.target.value)}
                rows={2}
                placeholder="Describe the competition…"
                className="w-full px-2.5 py-1.5 rounded-lg border border-border text-xs text-foreground bg-background resize-none focus:outline-none focus:ring-2 focus:ring-primary transition-all"
              />
            </div>

            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="text-xs text-muted-foreground mb-0.5 block">Funding Needed ($)</label>
                <input
                  type="text"
                  value={editFundingNeeded}
                  onChange={(e) => setEditFundingNeeded(e.target.value)}
                  placeholder="e.g. 50000"
                  className="w-full px-2.5 py-1.5 rounded-lg border border-border text-xs text-foreground bg-background focus:outline-none focus:ring-2 focus:ring-primary transition-all"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-0.5 block">Price / Share ($)</label>
                <input
                  type="text"
                  value={editPricePerShare}
                  onChange={(e) => setEditPricePerShare(e.target.value)}
                  placeholder="e.g. 10"
                  className="w-full px-2.5 py-1.5 rounded-lg border border-border text-xs text-foreground bg-background focus:outline-none focus:ring-2 focus:ring-primary transition-all"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-0.5 block">Min. Invest ($)</label>
                <input
                  type="text"
                  value={editInvest}
                  onChange={(e) => setEditInvest(e.target.value)}
                  placeholder="e.g. 500"
                  className="w-full px-2.5 py-1.5 rounded-lg border border-border text-xs text-foreground bg-background focus:outline-none focus:ring-2 focus:ring-primary transition-all"
                />
              </div>
            </div>

            {/* Financial Projections */}
            <div className="space-y-2">
              <p className="text-xs font-bold text-foreground">Financial Projections (USD)</p>
              {/* Revenue */}
              <div className="rounded-lg border border-success/20 bg-success/5 p-2">
                <p className="text-xs font-semibold text-success mb-1.5">Revenue</p>
                <div className="grid grid-cols-4 gap-1">
                  {(['m6', 'm12', 'm18', 'm24'] as const).map((k, i) => (
                    <div key={k}>
                      <label className="text-xs text-muted-foreground block mb-0.5">{['6M', '12M', '18M', '24M'][i]}</label>
                      <div className="relative">
                        <span className="absolute left-1.5 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">$</span>
                        <input
                          type="text"
                          value={editRevenue[k]}
                          onChange={(e) => handleRevenueChange(k, e.target.value)}
                          placeholder="0"
                          className="w-full pl-4 pr-1.5 py-1 rounded border border-border text-xs text-foreground bg-background focus:outline-none focus:ring-1 focus:ring-primary transition-all"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {/* Expenses */}
              <div className="rounded-lg border border-danger/20 bg-danger/5 p-2">
                <p className="text-xs font-semibold text-danger mb-1.5">Expenses</p>
                <div className="grid grid-cols-4 gap-1">
                  {(['m6', 'm12', 'm18', 'm24'] as const).map((k, i) => (
                    <div key={k}>
                      <label className="text-xs text-muted-foreground block mb-0.5">{['6M', '12M', '18M', '24M'][i]}</label>
                      <div className="relative">
                        <span className="absolute left-1.5 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">$</span>
                        <input
                          type="text"
                          value={editExpenses[k]}
                          onChange={(e) => handleExpensesChange(k, e.target.value)}
                          placeholder="0"
                          className="w-full pl-4 pr-1.5 py-1 rounded border border-border text-xs text-foreground bg-background focus:outline-none focus:ring-1 focus:ring-primary transition-all"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {/* Profit (auto-calculated) */}
              <div className="rounded-lg border border-primary/20 bg-primary/5 p-2">
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-xs font-semibold text-primary">Profit</p>
                  <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded-full">Auto-calculated</span>
                </div>
                <div className="grid grid-cols-4 gap-1">
                  {(['m6', 'm12', 'm18', 'm24'] as const).map((k, i) => {
                    const val = parseFloat(editProfit[k]) || 0;
                    return (
                      <div key={k}>
                        <label className="text-xs text-muted-foreground block mb-0.5">{['6M', '12M', '18M', '24M'][i]}</label>
                        <div className={`px-1.5 py-1 rounded border border-border text-xs font-semibold text-center ${val >= 0 ? 'text-success' : 'text-danger'} bg-muted/40`}>
                          {val >= 0 ? '+' : ''}{val.toLocaleString()}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {editError && <p className="text-xs text-danger">{editError}</p>}
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={saving}
                className="flex items-center gap-1 px-3 py-1.5 bg-primary text-primary-foreground text-xs font-bold rounded-lg hover:bg-cyan-600 transition-colors disabled:opacity-60"
              >
                <Check size={11} /> {saving ? 'Saving…' : 'Save'}
              </button>
              <button
                type="button"
                onClick={() => setShowEditForm(false)}
                className="flex items-center gap-1 px-3 py-1.5 bg-muted text-foreground text-xs font-semibold rounded-lg hover:bg-muted/80 transition-colors"
              >
                <X size={11} /> Cancel
              </button>
            </div>
          </form>
        )}

        {/* Title */}
        <h4 className="text-sm font-bold text-foreground leading-snug">{solution.title}</h4>

        {/* Excerpt */}
        <p className="text-xs text-muted-foreground leading-relaxed line-clamp-3">{solution.excerpt}</p>

        {/* Tags */}
        <div className="flex flex-wrap gap-1.5">
          {solution.tags.map((tag) => (
            <span key={`sol-tag-${solution.id}-${tag}`} className="text-xs font-medium bg-secondary text-secondary-foreground px-2 py-0.5 rounded-md">
              {tag}
            </span>
          ))}
        </div>

        {/* Funding */}
        <div className="bg-muted/50 rounded-lg p-2.5">
          <div className="flex justify-between text-xs mb-1.5">
            <span className="font-semibold text-foreground tabular-nums">
              ${(solution.fundingOffered / 1000).toFixed(0)}K offered
            </span>
            <span className="text-muted-foreground tabular-nums">
              of ${(solution.fundingAsk / 1000).toFixed(0)}K ask
            </span>
          </div>
          <div className="h-1.5 bg-border rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${fundingPct >= 100 ? 'bg-success' : 'bg-primary'}`}
              style={{ width: `${Math.min(fundingPct, 100)}%` }}
            />
          </div>
          <p className="text-xs text-muted-foreground mt-1">{fundingPct}% of ask covered · {solution.fundingOffers.length} offer{solution.fundingOffers.length !== 1 ? 's' : ''}</p>
        </div>

        {/* Entrepreneur + Rating */}
        <div className="flex items-center justify-between pt-1 border-t border-border">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-violet-100 flex items-center justify-center">
              <span className="text-xs font-bold text-violet-600">{solution.entrepreneur.charAt(0)}</span>
            </div>
            <div>
              <p className="text-xs font-semibold text-foreground leading-none">{solution.entrepreneur}</p>
              <p className="text-xs text-success font-medium">⭐ {solution.entrepreneurReputation}</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <StarRating value={Math.round(solution.rating)} size={11} />
            <span className="text-xs font-semibold text-foreground">{solution.rating}</span>
            <span className="text-xs text-muted-foreground">({solution.ratingCount})</span>
          </div>
        </div>

        {/* Expand/Collapse full details */}
        {hasDetails && (
          <button
            onClick={() => setExpanded((v) => !v)}
            className="w-full flex items-center justify-center gap-1.5 py-1.5 text-xs font-semibold text-primary hover:text-cyan-600 transition-colors"
          >
            {expanded ? <><ChevronUp size={13} /> Hide Details</> : <><ChevronDown size={13} /> View Full Proposal</>}
          </button>
        )}

        {/* Full Entrepreneur Form Details */}
        {expanded && hasDetails && (
          <div className="space-y-3 pt-1 border-t border-border">
            {solution.problem && (
              <div>
                <p className="text-xs font-semibold text-foreground uppercase tracking-wider mb-1">Problem Being Solved</p>
                <p className="text-xs text-muted-foreground leading-relaxed bg-muted/40 rounded-lg p-2.5">{solution.problem}</p>
              </div>
            )}

            {solution.solution && (
              <div>
                <p className="text-xs font-semibold text-foreground uppercase tracking-wider mb-1">Proposed Solution</p>
                <p className="text-xs text-muted-foreground leading-relaxed bg-muted/40 rounded-lg p-2.5">{solution.solution}</p>
              </div>
            )}

            {solution.productService && (
              <div>
                <p className="text-xs font-semibold text-foreground uppercase tracking-wider mb-1 flex items-center gap-1"><Zap size={10} className="text-primary" /> Product / Service</p>
                <p className="text-xs text-muted-foreground leading-relaxed bg-muted/40 rounded-lg p-2.5">{solution.productService}</p>
              </div>
            )}

            {solution.marketSize && (
              <div>
                <p className="text-xs font-semibold text-foreground uppercase tracking-wider mb-1 flex items-center gap-1"><Users size={10} className="text-primary" /> Market Size</p>
                <p className="text-xs text-muted-foreground leading-relaxed bg-muted/40 rounded-lg p-2.5">{solution.marketSize}</p>
              </div>
            )}

            {solution.competition && (
              <div>
                <p className="text-xs font-semibold text-foreground uppercase tracking-wider mb-1">Competition</p>
                <p className="text-xs text-muted-foreground leading-relaxed bg-muted/40 rounded-lg p-2.5">{solution.competition}</p>
              </div>
            )}

            {(solution.fundingNeeded || solution.pricePerShare || solution.invest) && (
              <div>
                <p className="text-xs font-semibold text-foreground uppercase tracking-wider mb-2 flex items-center gap-1"><DollarSign size={10} className="text-primary" /> Funding Details</p>
                <div className="grid grid-cols-3 gap-2">
                  {solution.fundingNeeded && (
                    <div className="bg-muted/40 rounded-lg p-2.5 text-center">
                      <p className="text-xs text-muted-foreground mb-0.5">Funding Needed</p>
                      <p className="text-xs font-bold text-foreground">${Number(solution.fundingNeeded).toLocaleString()}</p>
                    </div>
                  )}
                  {solution.pricePerShare && (
                    <div className="bg-muted/40 rounded-lg p-2.5 text-center">
                      <p className="text-xs text-muted-foreground mb-0.5">Price / Share</p>
                      <p className="text-xs font-bold text-foreground">${solution.pricePerShare}</p>
                    </div>
                  )}
                  {solution.invest && (
                    <div className="bg-muted/40 rounded-lg p-2.5 text-center">
                      <p className="text-xs text-muted-foreground mb-0.5">Min. Invest</p>
                      <p className="text-xs font-bold text-foreground">${Number(solution.invest).toLocaleString()}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {(solution.revenue || solution.expenses || solution.profit) && (
              <div>
                <p className="text-xs font-semibold text-foreground uppercase tracking-wider mb-2 flex items-center gap-1"><BarChart2 size={10} className="text-primary" /> Financial Projections</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-1.5 pr-2 text-muted-foreground font-semibold">Metric</th>
                        <th className="text-right py-1.5 px-2 text-muted-foreground font-semibold">6M</th>
                        <th className="text-right py-1.5 px-2 text-muted-foreground font-semibold">12M</th>
                        <th className="text-right py-1.5 px-2 text-muted-foreground font-semibold">18M</th>
                        <th className="text-right py-1.5 pl-2 text-muted-foreground font-semibold">24M</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border/50">
                      {solution.revenue && (
                        <tr>
                          <td className="py-1.5 pr-2 text-foreground font-medium">Revenue</td>
                          <td className="text-right py-1.5 px-2 text-success tabular-nums">${Number(solution.revenue.m6).toLocaleString()}</td>
                          <td className="text-right py-1.5 px-2 text-success tabular-nums">${Number(solution.revenue.m12).toLocaleString()}</td>
                          <td className="text-right py-1.5 px-2 text-success tabular-nums">${Number(solution.revenue.m18).toLocaleString()}</td>
                          <td className="text-right py-1.5 pl-2 text-success tabular-nums">${Number(solution.revenue.m24).toLocaleString()}</td>
                        </tr>
                      )}
                      {solution.expenses && (
                        <tr>
                          <td className="py-1.5 pr-2 text-foreground font-medium">Expenses</td>
                          <td className="text-right py-1.5 px-2 text-danger tabular-nums">${Number(solution.expenses.m6).toLocaleString()}</td>
                          <td className="text-right py-1.5 px-2 text-danger tabular-nums">${Number(solution.expenses.m12).toLocaleString()}</td>
                          <td className="text-right py-1.5 px-2 text-danger tabular-nums">${Number(solution.expenses.m18).toLocaleString()}</td>
                          <td className="text-right py-1.5 pl-2 text-danger tabular-nums">${Number(solution.expenses.m24).toLocaleString()}</td>
                        </tr>
                      )}
                      {solution.profit && (
                        <tr>
                          <td className="py-1.5 pr-2 text-foreground font-medium">Profit</td>
                          {(['m6','m12','m18','m24'] as const).map((m) => {
                            const val = Number(solution.profit![m]);
                            return (
                              <td key={m} className={`text-right py-1.5 px-2 tabular-nums font-semibold ${val >= 0 ? 'text-success' : 'text-danger'}`}>
                                {val >= 0 ? '+' : ''}{val.toLocaleString()}
                              </td>
                            );
                          })}
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Document Buttons — always visible */}
        <div className="flex gap-2 pt-1 border-t border-border">
          <DocButton
            label="Business Plan"
            docType="business_plan"
            solutionId={solution.id}
            currentUserId={currentUserId}
            isOwner={isOwner}
            isAdmin={isAdmin}
            isDbSolution={isDbSolution}
          />
          <DocButton
            label="Police Clearance"
            docType="police_clearance"
            solutionId={solution.id}
            currentUserId={currentUserId}
            isOwner={isOwner}
            isAdmin={isAdmin}
            isDbSolution={isDbSolution}
          />
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          {solution.entrepreneurVerified ? (
            <button
              onClick={onFundClick}
              className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-primary text-primary-foreground text-xs font-semibold rounded-lg hover:bg-cyan-600 active:scale-95 transition-all duration-150"
            >
              <DollarSign size={11} /> Submit Funding Offer
            </button>
          ) : (
            <div
              className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-muted text-muted-foreground text-xs font-semibold rounded-lg cursor-not-allowed border border-border"
              title="Entrepreneur must verify before funding is available"
            >
              <Lock size={11} /> Funding Locked — Not Verified
            </div>
          )}

          {isDbSolution && (
            <Link
              href={`/solution/${solution.id}`}
              className="flex items-center justify-center gap-1 px-3 py-2 border border-border text-xs font-semibold text-muted-foreground hover:text-foreground hover:border-primary/40 rounded-lg transition-colors"
              title="View full solution details"
            >
              <ExternalLink size={11} />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}